'use client';

import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextLink } from '@prismicio/next';
import { FC } from 'react';
import { PlainRightArrow } from '@/assets/PlainRightArrow';
import clsx from 'clsx';
import StatItem from './StatItem';
import { MotionSafeVideo } from '@/components/common/MotionSafeVideo';

export type HeroForVelxProps =
  SliceComponentProps<Content.HeroWithNavigationSlice>;

const HeroForVelx: FC<HeroForVelxProps> = ({ slice }) => {
  const { background_video, title, subtitle, primary_cta } = slice.primary;
  // `stats` is a custom field added in Prismic and may not be present in the generated types yet.
  // Use proper type casting to safely access it until the types are updated.
  const statsField = (slice.primary as unknown as { stats?: unknown[] }).stats;
  const stats = Array.isArray(statsField) ? statsField : undefined;
  const title_second_row =
    slice.variation === 'v2' ? slice.primary.title_second_row : undefined;

  // Server-rendered debug dump is used (if NODE_ENV === 'development') so
  // we don't need client-only hooks and avoid hydration mismatches.

  return (
    <section className="hero-section">
      {isFilled.linkToMedia(background_video) && (
        <MotionSafeVideo
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          className="background-video"
        >
          <source src={background_video.url} type="video/webm" />
          Your browser does not support the video tag.
        </MotionSafeVideo>
      )}

      {/* Overlay negro */}
      <div className="overlay" />

      {/* Gradiente que funde el video con el siguiente slice */}
      <div className="bottom-gradient" />

      <div className="content-shell">
        <main className="content-main">
          <PrismicRichText
            field={title}
            components={{
              heading1: ({ children }) => (
                <h1
                  className={
                    slice.variation === 'default'
                      ? clsx(
                          'h1-hero text-center tracking-normal',
                          'font-antonio',
                        )
                      : clsx(
                          'h1-hero text-center tracking-normal',
                          'font-inter',
                        )
                  }
                >
                  {children}
                </h1>
              ),
            }}
          />
          {slice.variation === 'v2' && (
            <PrismicRichText
              field={title_second_row}
              components={{
                heading1: ({ children }) => (
                  <h1
                    className={clsx(
                      'h1-hero text-center tracking-normal green-header',
                      'font-inter',
                    )}
                  >
                    {children}
                  </h1>
                ),
              }}
            />
          )}
          <PrismicRichText
            field={subtitle}
            components={{
              paragraph: ({ children }) => (
                <p className="mt-4 text-[20px] md:text-[24px] font-medium leading-[1.4] text-center text-gray-300 font-inter">
                  {children}
                </p>
              ),
            }}
          />
          <div className="mt-5">
            {slice.variation === 'default' &&
              primary_cta?.text &&
              isFilled.link(primary_cta) && (
                <PrismicNextLink
                  field={primary_cta}
                  className={clsx('primary-cta', 'group hover:no-underline')}
                >
                  {primary_cta.text}
                  <PlainRightArrow
                    className="ml-2 transition-transform group-hover:translate-x-1"
                    height={20}
                    width={20}
                    arrowFill="black"
                  />
                </PrismicNextLink>
              )}

            {slice.variation === 'v2' && (
              <div className={clsx('green-pill')}>
                <PrismicNextLink
                  field={primary_cta}
                  className={clsx('group hover:no-underline')}
                >
                  {primary_cta.text}
                </PrismicNextLink>
              </div>
            )}
          </div>
          {/* Stats (2-3 items) */}
          {Array.isArray(stats) && stats.length > 0 && (
            <div className="stats-row">
              <div className="stat-list">
                {stats
                  .filter(
                    (item): item is Record<string, unknown> =>
                      typeof item === 'object' && item !== null,
                  )
                  .map((item, idx) => (
                    <StatItem key={idx} item={item} />
                  ))}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Styled-jsx for component-specific styles */}
      <style jsx>{`
        .hero-section {
          position: relative;
          height: 100vh;
          width: 100%;
          overflow: hidden;
          color: white;
          background: linear-gradient(180deg, #0e1010 0%, #0f423a 100%);
        }

        /* Mobile landscape: a full 100vh hero swallows the short viewport and
           hides everything below it (audit M-42). Let it size to content. */
        @media (orientation: landscape) and (max-height: 600px) {
          .hero-section {
            height: auto;
            min-height: 480px;
          }
        }

        .background-video {
          position: absolute;
          inset: 0;
          z-index: 0;
          height: 100%;
          width: 100%;
          object-fit: cover;
          object-position: top;
        }

        @media (min-width: 768px) {
          .background-video {
            object-position: center;
          }
        }

        .overlay {
          position: absolute;
          inset: 0;
          z-index: 10;
          background-color: black;
          opacity: 0.5;
        }

        .bottom-gradient {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 8rem;
          z-index: 20;
          pointer-events: none;
          background: linear-gradient(
            180deg,
            rgba(14, 16, 16, 0) 0%,
            #0e1010 100%
          );
        }

        @media (min-width: 768px) {
          .bottom-gradient {
            height: 12rem;
          }
        }

        .content-shell {
          position: relative;
          z-index: 30;
          display: flex;
          height: 100%;
          flex-direction: column;
          color: white;
          padding-left: 30px;
          padding-right: 30px;
          padding-top: 148px;
          padding-bottom: 46px;
        }

        @media (min-width: 768px) {
          .content-shell {
            padding-left: 100px;
            padding-right: 100px;
          }
        }

        .content-main {
          margin-left: auto;
          margin-right: auto;
          display: flex;
          flex-grow: 1;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        .primary-cta {
          display: inline-flex;
          align-items: center;
          padding-left: 1.5rem;
          padding-right: 1.5rem;
          padding-top: 0.625rem;
          padding-bottom: 0.625rem;
          font-size: 1rem;
          font-weight: 700;
          line-height: 1.625;
          letter-spacing: normal;
          transition: background-color 0.3s;
          border-radius: 8px;
          background: #05f273;
          color: #0e1010;
        }

        .stats-row {
          margin-top: 2rem;
          display: flex;
          justify-content: center;
        }

        .stat-list {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 1rem;
        }

        :global(.stat-item) {
          border-radius: 24px;
          padding-left: 1.5rem;
          padding-right: 1.5rem;
          padding-top: 0.75rem;
          padding-bottom: 0.75rem;
          min-width: 140px;
          display: flex;
          flex-direction: column;
          align-items: center;
          background: rgba(10, 39, 34, 0.8);
          border: 1px solid rgba(5, 242, 115, 0.3);
          backdrop-filter: blur(12px);
        }

        .green-header {
          font-style: normal;
          font-weight: 700;
          font-size: 56px;
          line-height: 120%;
          text-align: center;
          color: #00f557;
        }

        .green-pill {
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;
          padding: 12px 24px;
          gap: 8px;

          background: #00f557;
          border-top: 1px solid #eaeaea;
          border-radius: 100px;

          font-style: normal;
          font-weight: 700;
          font-size: 16px;
          line-height: 150%;
          color: #002726;
          cursor: pointer;
        }

        .green-pill:hover {
          background-color: rgb(5, 242, 115);
          background-image: linear-gradient(
            to right,
            rgb(0, 242, 165),
            rgb(0, 194, 255)
          );
          --tw-shadow: 0 0 15px #00f2a5cc;
          box-shadow:
            var(--tw-ring-offset-shadow, 0 0 #0000),
            var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
          transition-duration: 0.3s;
          transition-property: all;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
    </section>
  );
};

export default HeroForVelx;
